import type { VehicleType } from "~/lib/constants/vehicle";
import { buildSearchUrl } from "~/lib/search-url";
import {
  floorTo15,
  SLOT_MINUTES,
  toLocalISO,
  wallClockNowIn,
} from "~/lib/utils/datetime";
import type { DriverEventDay } from "~/server/api/routers/events";
import { eventTimeZone } from "./driver-events";

/**
 * Turns a tapped event day into a `/search` URL that lands on results.
 *
 * The search flow is untouched by this: it reads the same params `SearchCard`
 * writes, built with the same `buildSearchUrl`. Events adapt to that contract
 * rather than the contract bending for events — which is also why a missing
 * vehicle size returns `null` here instead of being defaulted. `vehicle_size`
 * is load-bearing three separate times downstream (`isRunnable` here,
 * `SearchCard.canSearch`, and `SearchView.isResultsMode`), and it filters which
 * spots come back, so guessing "standard" for a truck driver produces a list
 * that doesn't fit their car with nothing on screen to explain why. The caller
 * asks instead — see `useOpenEventSearch`.
 */

/** Default booking length when the occurrence's own window is unusably short. */
const DEFAULT_DURATION_MS = 60 * 60_000;

/**
 * Both ends of the window as wall clocks *at the venue*, which is the only form
 * `/search` accepts (`YYYY-MM-DDTHH:mm`, reinterpreted there against the
 * destination's zone).
 *
 * Converting through the venue's zone rather than passing the ISO instant is
 * the whole point: a Californian opening a New York show would otherwise send
 * their own 4:00 PM reading of a 7:00 PM instant, and `/search` would re-read
 * that as 4:00 PM Eastern — a three-hour error that no screen would report.
 *
 * The start is clamped forward to the current slot because a day already under
 * way starts in the past and a booking cannot; the end is then held at least one
 * slot beyond it, so a nearly-over occurrence can't produce a zero-length
 * window. Both mirror what `resolveSearchWindow` does for a fresh search, so the
 * URL this writes is already in the form the search page would have corrected it
 * to — it never renders a window it then silently changes.
 */
function eventBookingWindow(
  day: Pick<DriverEventDay, "startsAt" | "endsAt">,
  timeZone: string | undefined,
  now: Date,
): { starts_at: string; ends_at: string } | null {
  const startMs = Date.parse(day.startsAt);
  const endMs = Date.parse(day.endsAt);
  if (Number.isNaN(startMs) || Number.isNaN(endMs)) return null;

  // Every value below is a *venue* wall clock carried in a viewer-local `Date`
  // (what `wallClockNowIn` returns), so they are all in one frame and compare
  // and add correctly against each other. None of them is an instant.
  const earliest = floorTo15(wallClockNowIn(now, timeZone));
  const occurrenceStart = floorTo15(
    wallClockNowIn(new Date(startMs), timeZone),
  );
  const start = occurrenceStart > earliest ? occurrenceStart : earliest;

  const occurrenceEnd = floorTo15(wallClockNowIn(new Date(endMs), timeZone));
  const minEnd = new Date(start.getTime() + SLOT_MINUTES * 60_000);
  const end =
    occurrenceEnd > minEnd
      ? occurrenceEnd
      : new Date(start.getTime() + DEFAULT_DURATION_MS);

  return { starts_at: toLocalISO(start), ends_at: toLocalISO(end) };
}

/**
 * The destination label — what the search pill reads back.
 *
 * Venue first: "Oracle Park" is what a driver recognises and what they'd have
 * typed, while the event name ("Giants vs. Dodgers") names an occasion, not a
 * place. The locality is appended because it is what a Mapbox-resolved label
 * looks like, and downstream service-area checks look for a city inside the
 * address string.
 */
export function eventDestinationLabel(event: {
  name: string;
  venueName: string | null;
  city: string | null;
  state: string | null;
}): string {
  return [event.venueName ?? event.name, event.city, event.state]
    .filter((part): part is string => !!part && part.trim().length > 0)
    .join(", ");
}

/**
 * Whether `buildEventSearchUrl` can produce a URL for this day — i.e. whether a
 * card for it would do anything at all when clicked.
 *
 * The vehicle size is deliberately not a parameter: it is the one input that
 * comes from the driver rather than the row, and the caller resolves it after
 * the click. This answers only "is the ROW usable", which is what the router
 * needs to decide whether to serve it.
 *
 * It restates `buildEventSearchUrl`'s null paths rather than calling it, because
 * the router runs this over every row of the feed and building a URL means a
 * timezone conversion per row. `event-search.test.ts` asserts the two agree on
 * every case either has ever returned null for — that test is what keeps the
 * restatement honest, so extend it alongside any new null path here.
 */
export function canOpenEventSearch(
  day: Pick<DriverEventDay, "startsAt" | "endsAt" | "event">,
): boolean {
  const { event } = day;
  return (
    Number.isFinite(event.lat) &&
    Number.isFinite(event.lng) &&
    !Number.isNaN(Date.parse(day.startsAt)) &&
    !Number.isNaN(Date.parse(day.endsAt)) &&
    // `/search` refuses to run without a destination label, and an event with a
    // blank name and no venue, city or state leaves nothing to build one from.
    eventDestinationLabel(event).length > 0
  );
}

/**
 * The `/search` URL for an event day, or `null` when it can't be built —
 * unparseable timestamps, a venue with no coordinates, nothing to name the
 * destination with, or no vehicle size.
 *
 * `now` is injectable so a caller can pass one clock for a whole render, and so
 * the clamp is testable.
 */
export function buildEventSearchUrl(
  day: DriverEventDay,
  vehicleSize: VehicleType | null,
  now: Date = new Date(),
): string | null {
  const { event } = day;
  if (!Number.isFinite(event.lat) || !Number.isFinite(event.lng)) return null;

  const window = eventBookingWindow(day, eventTimeZone(event), now);
  if (!window) return null;

  return buildSearchUrl({
    q: eventDestinationLabel(event),
    place: {
      lat: event.lat,
      lng: event.lng,
      name: event.venueName ?? event.name,
      city: event.city,
      state: event.state,
    },
    starts_at: window.starts_at,
    ends_at: window.ends_at,
    vehicleSize,
  });
}
